import { DownloadIcon, UploadIcon } from '@assets/icons/minor';
import { ImageryButton } from '@components/shared/ImageryButton/ImageryButton';
import { useNotificationsCenter } from '@contexts/NotificationsCenterContext';
import { useAoisNotifications } from '@hooks/queries/notifications/useAoisNotifications';
import { downloadFile } from '@hooks/queries/prescriptions/prescriptions.utils';
import dayjs from '@utils/dateUtils';
import { capitalizeString } from '@utils/index';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Amplitude } from 'src/amplitude';
import type { NotificationCenter } from '../NotificationCenter.models';
import { BaseNotification } from './BaseNotification';

function AoisNotificationAction({
  notification,
}: {
  readonly notification: NotificationCenter.Notification;
}) {
  const { t } = useTranslation();
  const { propertyId } = useParams();
  const { data } = useAoisNotifications({ propertyId });
  const { markAsRead } = useNotificationsCenter();

  const details = data?.[notification.id];
  const downloadUrl = details?.type === 'DOWNLOADS' ? details.extra_attrs.download_url : undefined;

  const handleDownload = () => {
    Amplitude.trackNotificationsDownloadZipFile(notification.service);
    markAsRead(notification);
    downloadFile(downloadUrl ?? '');
  };

  if (!details) return null;

  return (
    <React.Fragment>
      <p className="text-sm text-gray-700 whitespace-pre-line mt-1">
        {t(`notifications.${notification.service}.${notification.trigger}`, {
          date: capitalizeString(dayjs(notification.date).format('MMMM DD, YYYY')),
        })}
      </p>
      <div className="flex gap-1">
        <p className="text-sm text-gray-700 whitespace-pre-line mt-1 font-semibold">
          {t(`notifications.${notification.service}.file_status`)}
        </p>
        <p className="text-sm text-gray-700 whitespace-pre-line mt-1">
          {t(`notifications.${notification.service}.${details.status.toLocaleLowerCase()}`)}
        </p>
      </div>
      {details.status === 'DONE' && downloadUrl ? (
        <div className="flex gap-3 mt-2">
          <ImageryButton type="softPrimary" onClick={handleDownload}>
            {t('notifications.actions.download_zip_file')}
          </ImageryButton>
        </div>
      ) : null}
    </React.Fragment>
  );
}

const ActionComponents: Record<
  string,
  React.FC<{ readonly notification: NotificationCenter.Notification }>
> = {
  image_catalog_file: AoisNotificationAction,
  drone_image_upload: AoisNotificationAction,
};

const NotificationIcons: Partial<
  Record<NotificationCenter.NotificationService, React.ReactNode>
> = {
  image_download: (
    <div className="p-1 bg-sy-neutral-10 rounded-lg">
      <DownloadIcon />
    </div>
  ),
  drone_upload: (
    <div className="p-1 bg-sy-neutral-10 rounded-lg">
      <UploadIcon />
    </div>
  ),
};

function AoisNotification({
  notification,
}: {
  readonly notification: NotificationCenter.Notification;
}) {
  const ActionComponent = ActionComponents[notification.trigger];
  const icon = NotificationIcons[notification.service];

  return (
    <BaseNotification notification={notification} icon={icon} ActionComponent={ActionComponent} />
  );
}

export { AoisNotification };
